import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, InputNumber, Alert, Typography } from 'antd';
import { instrumentConfigApi, InstrumentConfigResponse } from '../../../services/instrumentConfigApi';
import { isFuturesExchange } from '../../../utils/instrumentKind';
import { formatNumber } from '../../../utils/numberFormat';
import { newIdempotencyKey } from '../../../utils/idempotencyKey';
import { ImportInProgress, importInProgressOf } from '../../../services/importJobApi';
import ImportInProgressModal from '../../../components/ImportJobProgress/ImportInProgressModal';

const { Text } = Typography;

type Mode = 'DEPOSIT' | 'WITHDRAW';

interface Props {
  open: boolean;
  mode: Mode;
  config: InstrumentConfigResponse | null;
  /** Parent portfolio id — required to look up available-to-deposit. */
  portfolioId?: string | null;
  onCancel: () => void;
  onSuccess: (updated: InstrumentConfigResponse) => void;
}

const formatVND = (s: string): string => {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return formatNumber(n);
};

const DepositWithdrawModal: React.FC<Props> = ({ open, mode, config, portfolioId, onCancel, onSuccess }) => {
  const [form] = Form.useForm<{ amount: number }>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importConflict, setImportConflict] = useState<ImportInProgress | null>(null);
  const [capacity, setCapacity] = useState<{
    purchasingPower: string | null;
    cash?: string | null;
    totalDeposited: string;
    inHoldings?: string;
    pendingDeposits?: string;
    availableToDeposit: string | null;
    overAllocated?: string | null;
  } | null>(null);
  const [amountValue, setAmountValue] = useState<number | null>(null);
  // One key per intent: a retry after a timeout replays the first result instead of moving
  // the cash twice. Re-minted when the amount changes or the server refused the request.
  const idempotencyKeyRef = useRef<string>(newIdempotencyKey());
  const inFlightRef = useRef(false);

  useEffect(() => {
    idempotencyKeyRef.current = newIdempotencyKey();
  }, [open, mode, amountValue]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setError(null);
      setImportConflict(null);
      setSubmitting(false);
      setCapacity(null);
      setAmountValue(null);
    }
  }, [open, form]);

  // The account this symbol's cash actually moves in: a pair's derivatives leg has its own,
  // and reading the stock account's headroom capped its deposit at whatever that one had left.
  const cashPortfolioId = config?.portfolioId ?? portfolioId;

  // Fetch virtual purchasing power only when opening a DEPOSIT modal.
  useEffect(() => {
    if (!open || mode !== 'DEPOSIT' || !cashPortfolioId) return;
    let cancelled = false;
    (async () => {
      try {
        const cap = await instrumentConfigApi.getDepositCapacity(cashPortfolioId);
        if (!cancelled) setCapacity(cap);
      } catch {
        // Soft-fail: server still enforces the cap on submit.
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, cashPortfolioId]);

  if (!config) return null;

  const title = mode === 'DEPOSIT'
    ? `Deposit cash → ${config.exchange}:${config.symbol}`
    : `Withdraw cash ← ${config.exchange}:${config.symbol}`;

  const available = capacity?.availableToDeposit ? Number(capacity.availableToDeposit) : null;
  const currentCap = Number(config.currentCapital) || 0;
  // Symmetric caps: deposit can't exceed virtual pp; withdraw can't exceed the symbol's deposited capital.
  const inputMax = mode === 'DEPOSIT'
    ? (available ?? undefined)
    : (currentCap > 0 ? currentCap : undefined);
  const willExceed =
    amountValue != null &&
    inputMax != null &&
    amountValue > inputMax;
  // Cash sitting in this symbol's wallet that no position is holding. Capital above this is
  // in shares, so taking it out means the bot has to sell first: the engine liquidates the
  // shortfall rather than refusing. Worth saying before the user presses the button, since
  // moving money between two symbols looks like bookkeeping until an order shows up.
  const idleCash = Math.max(currentCap - (Number(config.holdingsCost) || 0), 0);
  // A futures leg holds collateral, not shares: cost is posted margin, qty is contracts.
  const futures = isFuturesExchange(config.exchange);
  const willSell = mode === 'WITHDRAW' && amountValue != null && amountValue > idleCash;

  const handleSubmit = async () => {
    // Belt and braces: antd already drops a superseded validateFields, so this only covers
    // a click landing after validation but before `submitting` reaches the button.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const { amount } = await form.validateFields();
      setSubmitting(true);
      setError(null);
      const key = idempotencyKeyRef.current;
      const updated = mode === 'DEPOSIT'
        ? await instrumentConfigApi.deposit(config.id, amount, key)
        : await instrumentConfigApi.withdraw(config.id, amount, key);
      onSuccess(updated);
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'errorFields' in e) {
        return;
      }
      // Surface backend rejection message (e.g. "deposit X exceeds your available pp …")
      const anyErr = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      // The server answered, so this intent is settled; a retry must be a new request.
      // No response (timeout / network) keeps the key so the retry reads the outcome.
      if (anyErr?.response?.status != null) idempotencyKeyRef.current = newIdempotencyKey();
      // An open import on this namespace: show its progress, not a dead-end error.
      const open409 = importInProgressOf(e);
      if (open409) {
        setImportConflict(open409);
        return;
      }
      const msg = anyErr?.response?.data?.message ?? anyErr?.message ?? String(e);
      setError(msg);
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={mode === 'DEPOSIT' ? 'Deposit' : 'Withdraw'}
      okButtonProps={{
        loading: submitting,
        disabled: willExceed,
        style: mode === 'DEPOSIT' ? { background: '#0ECB81', borderColor: '#0ECB81' } : { background: '#F6465D', borderColor: '#F6465D' },
      }}
      destroyOnClose
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {mode === 'DEPOSIT' ? 'Already deposited to this symbol:' : 'Max withdrawable from this symbol:'}
        </Text>{' '}
        <Text strong style={{ color: mode === 'WITHDRAW' ? '#F6465D' : undefined }}>
          {formatVND(config.currentCapital)} VND
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>{futures ? 'Margin posted:' : 'Position cost:'}</Text>{' '}
        <Text>{formatVND(config.holdingsCost)} VND</Text>
        <br />
        {mode === 'WITHDRAW' && (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>Idle cash:</Text>{' '}
            <Text strong>{formatVND(String(idleCash))} VND</Text>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              (free to withdraw, nothing is sold)
            </Text>
            <br />
          </>
        )}
        {futures ? (
          // Futures are not marked to market here (the server reports the capital), so a
          // "market value" line would only repeat it. What matters is how many are open.
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>Open contracts:</Text>{' '}
            <Text>{formatVND(config.heldQty)}</Text>
            {config.marketPrice ? (
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                (@ {formatVND(String(config.marketPrice))} pts)
              </Text>
            ) : null}
          </>
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>Market value:</Text>{' '}
            <Text>
              {formatVND(
                // Fallback = mark at cost, which is just the capital: currentCapital already
                // includes holdingsCost, so adding it back double-counts the position.
                config.marketValue ?? config.currentCapital,
              )} VND
            </Text>
            {config.marketPrice ? (
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                (@ {formatVND(String(config.marketPrice))} / share, qty {formatVND(config.heldQty)})
              </Text>
            ) : null}
          </>
        )}
        {mode === 'DEPOSIT' && capacity?.availableToDeposit !== undefined && (
          <>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>Available to deposit:</Text>{' '}
            <Text strong style={{ color: '#0ECB81' }}>
              {capacity.availableToDeposit !== null ? formatVND(capacity.availableToDeposit) : '—'} VND
            </Text>
            {/* Show the operand the cap is actually computed from: gross cash, not the
                purchasing power on the panel above. They differ by whatever the bot has
                reserved in resting buy orders, and a line that doesn't add up reads as a
                bug. Older servers don't send `cash` — fall back to pp. */}
            {(capacity.cash ?? capacity.purchasingPower) !== null && (
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                ({futures ? 'Futures account cash' : 'Broker cash'} {formatVND(String(capacity.cash ?? capacity.purchasingPower))} − unspent allocations{' '}
                {formatVND(
                  String(Number(capacity.totalDeposited) - Number(capacity.inHoldings ?? 0)),
                )}
                {capacity.pendingDeposits && Number(capacity.pendingDeposits) > 0
                  ? ` − pending deposits ${formatVND(capacity.pendingDeposits)}`
                  : ''}
                {capacity.inHoldings && Number(capacity.inHoldings) > 0
                  ? futures
                    ? `; ${formatVND(capacity.inHoldings)} posted as margin and no longer in account cash`
                    : `; ${formatVND(capacity.inHoldings)} already invested and no longer in broker cash`
                  : ''}
                )
              </Text>
            )}
            {capacity.overAllocated && Number(capacity.overAllocated) > 0 ? (
              <>
                <br />
                <Text type="danger" style={{ fontSize: 12 }}>
                  Allocations exceed the cash on this account by {formatVND(capacity.overAllocated)} VND. No
                  allocation can grow until cash comes back.
                </Text>
              </>
            ) : null}
          </>
        )}
      </div>
      <Form form={form} layout="vertical">
        <Form.Item
          name="amount"
          label="Amount (VND)"
          rules={[
            { required: true, message: 'Amount is required' },
            { type: 'number', min: 1, message: 'Amount must be > 0' },
          ]}
        >
          <InputNumber<number>
            style={{ width: '100%' }}
            min={1}
            max={inputMax}
            step={1000}
            formatter={(v) => (v === undefined || v === null ? '' : formatNumber(Number(v)))}
            parser={((v?: string) => (v ? Number(v.replace(/[^\d]/g, '')) : 0)) as never}
            onChange={(v) => setAmountValue(typeof v === 'number' ? v : null)}
          />
        </Form.Item>
      </Form>
      {willSell && !willExceed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={futures
            ? `Only ${formatVND(String(idleCash))} VND is free. The rest is posted as margin for open contracts and is released only when they are closed.`
            : `Only ${formatVND(String(idleCash))} VND is idle. The bot will sell shares to raise the rest — if it is stopped, the idle part is returned now and only the rest waits for the next start.`}
          description={futures ? undefined : 'Sold shares settle in T+2, and the price you get may differ from the market value shown above.'}
        />
      )}
      {willExceed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={
            mode === 'DEPOSIT'
              ? 'Deposit exceeds your available purchasing power. Reduce the amount or withdraw from other symbols first.'
              : `Withdraw exceeds the capital you've deposited to ${config.symbol}. You can only take back up to ${formatVND(String(currentCap))} VND.`
          }
        />
      )}
      <Alert
        type="info"
        showIcon
        style={{ marginTop: 8 }}
        message={
          futures
            ? mode === 'DEPOSIT'
              ? 'Adds collateral to the futures account for this leg. A running pair bot uses it from its next start.'
              : 'Takes collateral out of the futures account. Margin posted for open contracts stays until they are closed.'
            : mode === 'DEPOSIT'
              ? 'If the bot is running, this will be queued (PENDING) until the bot picks it up for this instrument.'
              : 'If the bot is running, it will cancel pending buys and sell holdings of this instrument to free the cash.'
        }
      />
      {error && (
        <Alert type="error" showIcon style={{ marginTop: 8 }} message={error} />
      )}
      <ImportInProgressModal conflict={importConflict} onClose={() => setImportConflict(null)} />
    </Modal>
  );
};

export default DepositWithdrawModal;
